/**
 * klaviyo-funnel.js
 * Gemeinsame Helfer für die Customer-Journey-Funnels (Wertetest & 6 Tipps).
 *
 * Ausschließlich Klaviyo Client-API (/client/-Endpunkte, Public Key).
 * KEIN Private Key, KEIN Profil-Read im Browser.
 *
 * Exponiert window.KlaviyoFunnel mit:
 *   configure({publicKey, listId, revision})
 *   upsertProfile({email, first_name, properties})   -> POST /client/profiles/
 *   subscribeDoi({email, first_name, listId?, customSource?}) -> POST /client/subscriptions/ (DOI)
 *   trackEvent({email, metricName, properties})      -> POST /client/events/
 *   captureUtmParams() / getStoredUtmParams()
 *   isKnownVisitor(mode) -> Promise<boolean>   (KYC-Fragen nur einmal zeigen)
 *   markKycDone()                              (nach Absenden mit sichtbaren KYC-Fragen)
 *   hasAnsweredHerkunft() / markHerkunftAnswered() (Thank-You-Herkunftsfrage nur einmal)
 *   validateEmail(email) / passesHoneypot(form)
 *   hashEmail(email) -> Promise<string>       (SHA-256 hex, lowercase/trim; "" bei Fehler)
 */
(function () {
  "use strict";

  var config = { publicKey: "", listId: "", revision: "2025-01-15" };

  function configure(opts) {
    if (!opts) return;
    if (opts.publicKey) config.publicKey = String(opts.publicKey).trim();
    if (opts.listId) config.listId = String(opts.listId).trim();
    if (opts.revision) config.revision = String(opts.revision).trim();
  }

  function headers() {
    return {
      revision: config.revision,
      "content-type": "application/vnd.api+json",
      accept: "application/vnd.api+json",
    };
  }

  function endpoint(path) {
    return (
      "https://a.klaviyo.com/client/" +
      path +
      "/?company_id=" +
      encodeURIComponent(config.publicKey)
    );
  }

  // (1) Profil anlegen/mergen inkl. Custom Properties
  // opts.appendProperties: { PropertyName: valueToAppend } -> hängt an eine
  //   Listen-Property an (Klaviyo meta.patch_properties.append), ohne den
  //   bestehenden Listeninhalt zu überschreiben. Legt die Liste an, falls neu.
  async function upsertProfile(opts) {
    opts = opts || {};
    var data = {
      type: "profile",
      attributes: {
        email: opts.email,
        first_name: opts.first_name,
        properties: opts.properties || {},
      },
    };
    if (opts.appendProperties && Object.keys(opts.appendProperties).length) {
      data.meta = { patch_properties: { append: opts.appendProperties } };
    }
    var payload = { data: data };
    var res = await fetch(endpoint("profiles"), {
      method: "POST",
      headers: headers(),
      body: JSON.stringify(payload),
    });
    if ([200, 201, 202, 409].indexOf(res.status) === -1) {
      var body = "";
      try {
        body = await res.text();
      } catch (e) {}
      throw new Error("Profile upsert failed: " + res.status + " " + body);
    }
    return res.status;
  }

  // (2) Listen-Anmeldung mit Double-Opt-In
  async function subscribeDoi(opts) {
    opts = opts || {};
    var payload = {
      data: {
        type: "subscription",
        attributes: {
          custom_source: opts.customSource || "Customer Journey Funnel",
          profile: {
            data: {
              type: "profile",
              attributes: { email: opts.email, first_name: opts.first_name },
            },
          },
        },
        relationships: {
          list: { data: { type: "list", id: opts.listId || config.listId } },
        },
      },
    };
    var res = await fetch(endpoint("subscriptions"), {
      method: "POST",
      headers: headers(),
      body: JSON.stringify(payload),
    });
    if (res.status !== 202) {
      var body = "";
      try {
        body = await res.text();
      } catch (e) {}
      throw new Error("Subscription failed: " + res.status + " " + body);
    }
    return res.status;
  }

  // (3) Event/Metrik feuern
  async function trackEvent(opts) {
    opts = opts || {};
    var payload = {
      data: {
        type: "event",
        attributes: {
          metric: {
            data: { type: "metric", attributes: { name: opts.metricName } },
          },
          profile: {
            data: { type: "profile", attributes: { email: opts.email } },
          },
          properties: opts.properties || {},
        },
      },
    };
    var res = await fetch(endpoint("events"), {
      method: "POST",
      headers: headers(),
      body: JSON.stringify(payload),
    });
    if (res.status !== 202) {
      var body = "";
      try {
        body = await res.text();
      } catch (e) {}
      // Events sind nicht kritisch für das UX -> nur warnen, nicht blockieren
      if (window.console) console.warn("Event failed: " + res.status + " " + body);
    }
    return res.status;
  }

  // --- UTM ---------------------------------------------------------------
  var UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];

  function getCookie(name) {
    var value = "; " + document.cookie;
    var parts = value.split("; " + name + "=");
    if (parts.length === 2) return parts.pop().split(";").shift();
    return null;
  }

  function readUrlUtms() {
    var out = {};
    try {
      var p = new URLSearchParams(window.location.search);
      UTM_KEYS.forEach(function (k) {
        var v = p.get(k);
        if (v) out[k] = v;
      });
    } catch (e) {}
    return out;
  }

  function readCookieUtms() {
    var c = getCookie("session_utms");
    if (!c) return {};
    try {
      return JSON.parse(decodeURIComponent(c)) || {};
    } catch (e) {
      return {};
    }
  }

  function readStoredUtms() {
    try {
      return JSON.parse(localStorage.getItem("cj_utm") || "{}") || {};
    } catch (e) {
      return {};
    }
  }

  // Beim Landing aufrufen: URL-UTM in localStorage sichern (Stealth-freundlich, überdauert Reloads)
  function captureUtmParams() {
    var url = readUrlUtms();
    if (Object.keys(url).length) {
      try {
        localStorage.setItem("cj_utm", JSON.stringify(url));
      } catch (e) {}
      return url;
    }
    return getStoredUtmParams();
  }

  // Zusammengeführte UTM (URL > localStorage > session_utms-Cookie)
  function getStoredUtmParams() {
    var url = readUrlUtms();
    var stored = readStoredUtms();
    var cookie = readCookieUtms();
    var out = {};
    UTM_KEYS.forEach(function (k) {
      var v = url[k] || stored[k] || cookie[k] || "";
      if (v) out[k] = v;
    });
    return out;
  }

  // --- Bekannter Besucher / KYC nur einmal --------------------------------
  // "Bekannt" heißt: Die KYC-Fragen (Erstes Buch, Kaufort) wurden schon einmal
  // gesehen. Signale: ?wt=1 in der URL (Link aus der Wertetest-Mail), das
  // Browser-Flag cj_kyc_done (wird beim Absenden von Wertetest ODER 6 Tipps mit
  // sichtbaren KYC-Fragen gesetzt), das Alt-Flag smt_wertetest_done, sowie je
  // nach Modus die Klaviyo-Identifikation (Cookie).
  var KYC_DONE_KEY = "cj_kyc_done";
  var HERKUNFT_DONE_KEY = "cj_herkunft_answered";

  function readFlag(key) {
    try {
      return localStorage.getItem(key) === "1";
    } catch (e) {
      return false;
    }
  }
  function writeFlag(key) {
    try {
      localStorage.setItem(key, "1");
    } catch (e) {}
  }

  function hasWtFlag() {
    try {
      var p = new URLSearchParams(window.location.search);
      if (p.get("wt") === "1") return true;
    } catch (e) {}
    if (readFlag(KYC_DONE_KEY)) return true;
    if (readFlag("smt_wertetest_done")) return true;
    return false;
  }

  // ?kyc=1 erzwingt die KYC-Fragen (Test / QA), unabhängig von allen Signalen
  function hasKycForceFlag() {
    try {
      var p = new URLSearchParams(window.location.search);
      return p.get("kyc") === "1";
    } catch (e) {
      return false;
    }
  }

  function markKycDone() {
    writeFlag(KYC_DONE_KEY);
  }

  // Thank-You-Herkunftsfrage: nur einmal pro Browser stellen
  function hasAnsweredHerkunft() {
    return readFlag(HERKUNFT_DONE_KEY);
  }
  function markHerkunftAnswered() {
    writeFlag(HERKUNFT_DONE_KEY);
  }

  function klaviyoIdentified() {
    try {
      if (window.klaviyo && typeof window.klaviyo.isIdentified === "function") {
        var r = window.klaviyo.isIdentified();
        if (r && typeof r.then === "function") {
          return r.then(function (v) {
            return !!v;
          }).catch(function () {
            return false;
          });
        }
        return Promise.resolve(!!r);
      }
    } catch (e) {}
    return Promise.resolve(false);
  }

  // mode: 'identified_or_wt' (default) | 'wt_only'  -> Promise<boolean>
  function isKnownVisitor(mode) {
    if (hasKycForceFlag()) return Promise.resolve(false);
    if (hasWtFlag()) return Promise.resolve(true);
    if (mode === "wt_only") return Promise.resolve(false);
    return klaviyoIdentified();
  }

  // --- Validierung -------------------------------------------------------
  function validateEmail(email) {
    if (!email || email.trim() === "") {
      return { valid: false, message: "Bitte gib eine E-Mail-Adresse ein." };
    }
    var re =
      /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;
    var t = email.trim();
    if (!re.test(t)) {
      return {
        valid: false,
        message: "Bitte gib eine gültige E-Mail-Adresse ein (z.B. name@beispiel.de).",
      };
    }
    if (t.length > 254) return { valid: false, message: "Die E-Mail-Adresse ist zu lang." };
    if (t.split("@")[0].length > 64) {
      return { valid: false, message: "Der Teil vor dem @ ist zu lang." };
    }
    return { valid: true, message: "" };
  }

  function passesHoneypot(form) {
    if (!form) return true;
    var hp = form.querySelector('input[name="website"]');
    return !(hp && hp.value);
  }

  // --- E-Mail-Hash (SHA-256) ---------------------------------------------
  // Normalisierung wie bei Google Enhanced Conversions / Klaviyo:
  // trim + lowercase, dann SHA-256 als Hex-String.
  // Wird als email_hash im Pixel-Event form_submit_c mitgeschickt (nie die
  // Klartext-Adresse) und dient in GA4 als User-ID zur geräteübergreifenden
  // Wiedererkennung. Gibt "" zurück, wenn WebCrypto nicht verfügbar ist.
  function hashEmail(email) {
    var normalized = (email || "").trim().toLowerCase();
    if (!normalized || !window.crypto || !window.crypto.subtle || !window.TextEncoder) {
      return Promise.resolve("");
    }
    try {
      var bytes = new TextEncoder().encode(normalized);
      return window.crypto.subtle.digest("SHA-256", bytes).then(function (buf) {
        var arr = Array.prototype.slice.call(new Uint8Array(buf));
        return arr.map(function (b) { return ("00" + b.toString(16)).slice(-2); }).join("");
      }).catch(function () { return ""; });
    } catch (e) {
      return Promise.resolve("");
    }
  }

  // --- Buchname aus QR-Kampagne ------------------------------------------
  // Nutzer scannen den QR-Code im Buch und landen mit
  //   utm_source=book&utm_medium=qr&utm_campaign=<Buchname>
  // auf der Seite. Nur in diesem Fall trägt utm_campaign den Buchnamen.
  function getScannedBook() {
    var utm = getStoredUtmParams();
    if ((utm.utm_source || "").toLowerCase() !== "book") return "";
    return (utm.utm_campaign || "").trim();
  }

  window.KlaviyoFunnel = {
    configure: configure,
    upsertProfile: upsertProfile,
    subscribeDoi: subscribeDoi,
    trackEvent: trackEvent,
    captureUtmParams: captureUtmParams,
    getStoredUtmParams: getStoredUtmParams,
    getScannedBook: getScannedBook,
    isKnownVisitor: isKnownVisitor,
    markKycDone: markKycDone,
    hasAnsweredHerkunft: hasAnsweredHerkunft,
    markHerkunftAnswered: markHerkunftAnswered,
    validateEmail: validateEmail,
    passesHoneypot: passesHoneypot,
    hashEmail: hashEmail,
  };

  // UTM früh sichern (auch wenn der User später ohne UTM erneut lädt)
  try {
    captureUtmParams();
  } catch (e) {}
})();
